import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { responseReport } from '../../lib/supportResponseTimes';
import { reportAsText, reportAsTsv, formatDuration, INPUT_SOURCE_LABEL } from '../../lib/supportResponseReportText';

const PARTY_BADGE = {
    staff: 'text-bg-primary',
    customer: 'text-bg-secondary',
    ai: 'text-bg-light',
    system: 'text-bg-light',
};

const PARTY_LABEL = {
    staff: 'Oss',
    customer: 'Kunde',
    ai: 'AI',
    system: 'System',
};

@observer
class ResponseReportDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = { copied: null };
        this.copyResetTimer = null;
    }

    componentWillUnmount() {
        if (this.copyResetTimer) {
            clearTimeout(this.copyResetTimer);
        }
    }

    closeDrawer = () => {
        const { appState } = this.props.stores;
        const { drawerLevel } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    copy = async (kind) => {
        const { ticket = {} } = this.props;
        const report = responseReport(ticket);
        const payload = kind === 'tsv' ? reportAsTsv(report) : reportAsText(ticket, report);
        try {
            if (!navigator.clipboard || !navigator.clipboard.writeText) {
                throw new Error('Clipboard API unavailable');
            }
            await navigator.clipboard.writeText(payload);
            this.setState({ copied: kind });
        } catch (err) {
            this.setState({ copied: 'error' });
        }
        if (this.copyResetTimer) {
            clearTimeout(this.copyResetTimer);
        }
        this.copyResetTimer = setTimeout(() => this.setState({ copied: null }), 2500);
    }

    renderPartySummary = (labelNode, party) => (
        <div class="mb-1">
            <strong>{labelNode}</strong>{' '}
            {party.count === 0
                ? <span class="text-body-secondary"><Text id='support.report.noAnswers'>no answers yet</Text></span>
                : (
                    <span>
                        {party.count} <Text id='support.report.answers'>answers</Text>
                        {' · '}<Text id='support.report.avg'>avg</Text> {formatDuration(party.avgMs)}
                        {party.count > 1 && (
                            <span>
                                {' · '}<Text id='support.report.fastest'>fastest</Text> {formatDuration(party.minMs)}
                                {' · '}<Text id='support.report.slowest'>slowest</Text> {formatDuration(party.maxMs)}
                            </span>
                        )}
                    </span>
                )}
        </div>
    )

    render() {
        const { ticket = {} } = this.props;
        const { copied } = this.state;
        const report = responseReport(ticket);
        const { turns, staff, customer, perPerson, totals } = report;

        return (
            <div class="d-flex flex-column gap-3 p-4 mb-5">
                <h5 class="mb-1">
                    <i class="fas fa-stopwatch me-2" />
                    <Text id='support.report.title'>Response report</Text>
                    {!!ticket.ticketNumber && <span class="text-body-secondary ms-2">#{ticket.ticketNumber}</span>}
                </h5>
                <p class="text-body-secondary small mb-0">
                    <Text id='support.report.help'>
                        Elapsed wall-clock time between turns. AI auto-replies and status messages are listed but never counted.
                    </Text>
                </p>

                <div class="d-flex flex-wrap gap-2">
                    <button type="button" class="btn btn-sm btn-outline-primary" onClick={() => this.copy('text')}>
                        <i class="fas fa-copy me-1" />
                        <Text id='support.report.copyText'>Copy as text</Text>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary" onClick={() => this.copy('tsv')}>
                        <i class="fas fa-table me-1" />
                        <Text id='support.report.copySpreadsheet'>Copy for spreadsheet</Text>
                    </button>
                    {copied === 'text' && <span class="text-success small align-self-center"><Text id='support.report.copiedText'>Text copied</Text></span>}
                    {copied === 'tsv' && <span class="text-success small align-self-center"><Text id='support.report.copiedTsv'>Spreadsheet rows copied</Text></span>}
                    {copied === 'error' && <span class="text-danger small align-self-center"><Text id='support.report.copyFailed'>Could not copy — select the table and copy manually</Text></span>}
                </div>

                {turns.length === 0 ? (
                    <div class="alert alert-secondary mb-0">
                        <Text id='support.report.empty'>No messages with timestamps on this ticket yet.</Text>
                    </div>
                ) : (
                    <div class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col"><Text id='support.report.party'>Party</Text></th>
                                    <th scope="col"><Text id='support.report.name'>Name</Text></th>
                                    <th scope="col"><Text id='support.report.when'>When</Text></th>
                                    <th scope="col" class="text-end"><Text id='support.report.responseTime'>Response time</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {turns.map((turn, idx) => (
                                    <tr key={idx} class={turn.counted ? '' : 'opacity-75'}>
                                        <td class="text-body-secondary">{turn.counted ? turn.index : '–'}</td>
                                        <td>
                                            <span class={`badge ${PARTY_BADGE[turn.party] || 'text-bg-light'}`}>
                                                {PARTY_LABEL[turn.party] || turn.party}
                                            </span>
                                        </td>
                                        <td>
                                            {turn.name}
                                            {!!turn.loggedBy && (
                                                <small class="text-body-secondary d-block">
                                                    <Text id='support.report.loggedBy'>logged by</Text> {turn.loggedBy}
                                                    {!!turn.inputSource && ` (${INPUT_SOURCE_LABEL[turn.inputSource] || turn.inputSource})`}
                                                </small>
                                            )}
                                            {turn.messageCount > 1 && (
                                                <small class="text-body-secondary d-block">
                                                    <Text id='support.report.messageCount' fields={{ count: turn.messageCount }}>
                                                        {turn.messageCount} messages
                                                    </Text>
                                                </small>
                                            )}
                                        </td>
                                        <td class="text-nowrap">{turn.date ? turn.date.toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                                        <td class="text-end text-nowrap">
                                            {turn.counted
                                                ? <span>{formatDuration(turn.responseMs)}</span>
                                                : (
                                                    <span class="text-body-secondary small">
                                                        {turn.party === 'ai'
                                                            ? <Text id='support.report.notCounted'>auto, not counted</Text>
                                                            : <Text id='support.report.notCountedSystem'>not counted</Text>}
                                                    </span>
                                                )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div class="border-top pt-3">
                    {this.renderPartySummary(<Text id='support.report.us'>Us:</Text>, staff)}
                    {this.renderPartySummary(<Text id='support.report.customer'>Customer:</Text>, customer)}
                    <div class="text-body-secondary small mt-2">
                        <Text id='support.report.firstHuman'>First human reply:</Text>{' '}
                        {totals.firstHumanResponseMs === null
                            ? <Text id='support.report.noHumanReply'>no human replies</Text>
                            : formatDuration(totals.firstHumanResponseMs)}
                        {' · '}
                        <Text id='support.report.totalOpen'>Total open time:</Text> {formatDuration(totals.openMs)}
                        {totals.aiReplyCount > 0 && (
                            <span>
                                {' · '}
                                <Text id='support.report.aiReplies' fields={{ count: totals.aiReplyCount }}>
                                    {totals.aiReplyCount} AI replies (not counted)
                                </Text>
                            </span>
                        )}
                        {totals.undatedMessageCount > 0 && (
                            <span>
                                {' · '}
                                <Text id='support.report.undated' fields={{ count: totals.undatedMessageCount }}>
                                    {totals.undatedMessageCount} messages without a timestamp (excluded)
                                </Text>
                            </span>
                        )}
                    </div>
                </div>

                {perPerson.length > 0 && (
                    <div class="border-top pt-3">
                        <h6 class="text-uppercase text-muted fw-bold mb-2" style="font-size: 0.7rem;">
                            <Text id='support.report.perPerson'>Per staff member</Text>
                        </h6>
                        {perPerson.map(person => (
                            <div key={person.name} class="d-flex justify-content-between small">
                                <span>{person.name}</span>
                                <span class="text-body-secondary">
                                    {person.count} <Text id='support.report.answers'>answers</Text>
                                    {' · '}<Text id='support.report.avg'>avg</Text> {formatDuration(person.avgMs)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary" onClick={this.closeDrawer}>
                        <Text id='support.report.close'>Close</Text>
                    </button>
                </div>
            </div>
        );
    }
}

export default ResponseReportDrawer;
